// dependencies
import { FloatingPortal, autoPlacement, autoUpdate, offset, useFloating } from "@floating-ui/react";
import { animated, useSpring } from "@react-spring/web";
import React, { useEffect, useRef, useState } from "react";

// hooks
import { useUser } from "../../../../../api/hooks";

//utils
import { getEmployeeIdFromLink } from "../utils";

// components
import EmployeeCard from "..";

const EmployeeTooltipCardWrapper = () => {
	const [employeeID, setEmployeeID] = useState<string>("");
	const [isTornadoTemplate, setIsTornadoTemplate] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const visibilityTimeout = useRef<NodeJS.Timeout | null>(null);
	const [linkRef, setLinkRef] = useState<Element | null>(null);
	const cardRef = useRef<HTMLDivElement | null>(null);
	const currentRoute = window.location.pathname;

	const { data: employeeData, isFetching: isUserFetching } = useUser({
		userID: employeeID,
		disabled: !employeeID,
	});
	const [springs, api] = useSpring(
		() => ({
			from: { opacity: 0, marginTop: -4 },
			to: { opacity: 1, marginTop: 0 },
		}),
		[]
	);

	useEffect(() => {
		setEmployeeID("");
		setIsHovered(false);
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef?.current);
		}
	}, [currentRoute]);

	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (employeeData?.id) {
			if (isHovered) {
				api.start({ opacity: 1, config: { duration: 50 } });
			} else {
				api.start({ opacity: 0, marginTop: -4, config: { duration: 0 } });
				timeout = setTimeout(() => {
					setEmployeeID("");
				}, 200);
			}
		}

		return () => clearTimeout(timeout);
	}, [employeeData?.id, isHovered]);

	useEffect(() => {
		// if the link is iwithin id of react-app, set isTornadoTemplate to true
		const reactAppRef = document.getElementById("react-app");
		if (!reactAppRef || !reactAppRef.contains(linkRef)) {
			setIsTornadoTemplate(true);
		} else {
			setIsTornadoTemplate(false);
		}

		//These are jquery listeners to set the hover state of the component
		$(document).on("mouseenter", "[href^='/user/']", () => {
			// get hovered element with href that triggered this function
			const hoveredElement = document.querySelector("[href^='/user/']:hover");
			// if hovered element is not null and does not have id employee-card, and does not have classname no-tooltip
			//set linkRef to hovered element
			if (
				hoveredElement &&
				!hoveredElement.id.includes("employee-card") &&
				!hoveredElement.classList.contains("no-tooltip")
			) {
				// get href of hovered element
				const href = hoveredElement.getAttribute("href");
				if (href) {
					const employeeIdFromLink = getEmployeeIdFromLink(href);
					if (hoverTimeoutRef.current) {
						clearTimeout(hoverTimeoutRef.current);
					}
					if (visibilityTimeout.current) {
						clearTimeout(visibilityTimeout.current);
					}
					hoverTimeoutRef.current = setTimeout(() => {
						setEmployeeID(employeeIdFromLink);
						setLinkRef(hoveredElement);
						setIsHovered(true);
					}, 400);
				}
			}
		});

		$(document).on("mouseleave", "[href^='/user/']", () => {
			// if the cardRef is hovered or employee card is hovered, do nothing
			if (
				cardRef.current?.contains(document.querySelector(":hover")) ||
				document.querySelector("#employee-card:hover")
			) {
				return;
			}
			visibilityTimeout.current = setTimeout(() => {
				setIsHovered(false);
			}, 400);
		});

		// if we enter the employee card, clear the kill timer
		$(document).on("mouseenter", "#employee-card", () => {
			setIsHovered(true);
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			if (visibilityTimeout.current) {
				clearTimeout(visibilityTimeout.current);
			}
		});

		// if we leave the employee card, instantiate the kill timer
		$(document).on("mouseleave", "#employee-card", (e) => {
			// prevent mouseleave from firing if we're hovering over the tooltip within the employee card
			if ($(e.relatedTarget).closest("#avatar-tooltip").length) return;
			setIsHovered(false);
		});

		// Hide tooltip immediately when any link is clicked to prevent positioning issues during navigation
		$(document).on("click", "a[href]", () => {
			setIsHovered(false);
			setEmployeeID("");
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			if (visibilityTimeout.current) {
				clearTimeout(visibilityTimeout.current);
			}
		});

		return () => {
			$(document).off("mouseenter", "[href^='/user/']");
			$(document).off("mouseleave", "[href^='/user/']");
			$(document).off("mouseenter", "#employee-card");
			$(document).off("mouseleave", "#employee-card");
			$(document).off("click", "a[href]");
		};
	}, [employeeID, isHovered, linkRef]);

	const { refs, floatingStyles } = useFloating({
		placement: "bottom-start",
		strategy: "absolute",
		elements: {
			reference: linkRef,
		},
		middleware: [
			offset(8),
			autoPlacement({
				crossAxis: true,
				allowedPlacements: isTornadoTemplate ? undefined : ["bottom-start", "bottom-end"],
				padding: 4,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	if (!employeeData || !employeeID || isUserFetching) {
		return null;
	}

	return (
		<FloatingPortal id="curtain-scroll-container">
			<animated.div ref={refs.setFloating} style={{ ...floatingStyles, ...springs, zIndex: 100000 }}>
				<EmployeeCard employeeData={employeeData} />
			</animated.div>
		</FloatingPortal>
	);
};

export default EmployeeTooltipCardWrapper;
